/**
 * A mail's HTML as elements the till can show: the same picture the customer's mail app draws, in
 * its white or its dark version.
 *
 * The till never puts foreign HTML in its page. The mail is read apart (nothing runs, nothing loads)
 * and rebuilt piece by piece: only the elements and attributes of a mail's layout, links to the web,
 * mail or phone that open elsewhere, and no picture but the till's own (the Tafo logo, the pizza of
 * the Dagpromo), from the till itself. Styles are set one declaration at a time: the till's
 * Content-Security-Policy refuses style attributes and style sheets.
 *
 * It refuses them where the mail is read too. Nothing is drawn there, but the browser reports each one
 * (some ninety for one mail). So before the mail is read its styles are put aside: the style of an
 * element under another name, the style sheets as text.
 */

/** The version of a mail: the white one, or the one a mail app with a dark theme shows. */
export type MailScheme = "light" | "dark";

const ELEMENTS = new Set([
  "a",
  "b",
  "br",
  "div",
  "h1",
  "h2",
  "h3",
  "img",
  "p",
  "span",
  "strong",
  "table",
  "tbody",
  "td",
  "th",
  "tr",
]);

/** How a mail places and sizes things, besides its styles. */
const ATTRIBUTES = new Set([
  "align",
  "alt",
  "bgcolor",
  "border",
  "cellpadding",
  "cellspacing",
  "colspan",
  "height",
  "role",
  "rowspan",
  "valign",
  "width",
]);

/** Links a mail may carry; anything else (javascript:, data:) is left out. */
const LINK = /^(https?:|mailto:|tel:)/i;

/**
 * The pictures of the till a mail may show: the Tafo logo on every mail, and the pizza of the mail about the
 * Dagpromo. The till shows its own copy, whatever address the mail names.
 */
const LOGOS = new Set(["/tafo-logo.png", "/tafo-logo-wit.png", "/dagpromo-pizza.png"]);
/** The shop's own logo on top of a Rondzending, served by Tafo: the till fetches it from its own address. */
const SHOP_LOGO = /^\/api\/v1\/mail-logo\/[0-9a-f-]{36}$/;

/** Nothing is fetched for a style, and nothing leaves its place in the mail. */
const REFUSED_VALUE = /url\(|expression\(/i;
const REFUSED_PROPERTY = /^(position|z-index|behavior)$/i;

/** The name the style of an element has while the mail is read. */
const KEPT_STYLE = "data-mail-style";

const STYLE_SHEET = /<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi;
/** A start tag with its attributes; a ">" inside a quoted value does not end it. */
const START_TAG = /<([a-z][a-z0-9]*)\b((?:"[^"]*"|'[^']*'|[^"'<>])*)>/gi;
/** One attribute: its name, and "=" with its value when it has one. */
const ATTRIBUTE = /([^\s"'<>/=]+)(\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?/g;

/**
 * The mail without anything the policy would refuse: every style attribute under another name, and the
 * style sheets taken out, as text. What a text or another attribute says about "style" is left alone.
 */
function setStylesAside(html: string): { html: string; sheets: string } {
  let sheets = "";
  const withoutSheets = html.replace(STYLE_SHEET, (_element, sheet: string) => {
    sheets += `${sheet}\n`;
    return "";
  });
  const renamed = withoutSheets.replace(
    START_TAG,
    (_tag, name: string, attributes: string) =>
      `<${name}${attributes.replace(ATTRIBUTE, (attribute: string, key: string, value: string | undefined) =>
        key.toLowerCase() === "style" ? `${KEPT_STYLE}${value ?? ""}` : attribute,
      )}>`,
  );
  return { html: renamed, sheets };
}

function urlOf(address: string): URL | null {
  try {
    return new URL(address, window.location.href);
  } catch {
    return null;
  }
}

function setStyles(target: HTMLElement, declarations: string): void {
  for (const declaration of declarations.split(";")) {
    const colon = declaration.indexOf(":");
    if (colon < 1) continue;
    const property = declaration.slice(0, colon).trim();
    const value = declaration.slice(colon + 1).trim();
    if (REFUSED_PROPERTY.test(property) || REFUSED_VALUE.test(value)) continue;
    // an unknown property (Outlook's mso-…) is simply not kept
    target.style.setProperty(property, value);
  }
}

/**
 * The mail's dark version: what its style sheet gives each class in a mail app with a dark theme
 * ("@media (prefers-color-scheme:dark){.card{background:#17181b!important}…}"), per class.
 */
function darkVersion(sheet: string): Map<string, string> {
  const styles = new Map<string, string>();
  const dark = /@media\s*\(\s*prefers-color-scheme\s*:\s*dark\s*\)\s*\{((?:[^{}]*\{[^{}]*\})*)/i.exec(sheet);
  for (const [, name, declarations] of (dark?.[1] ?? "").matchAll(/\.([\w-]+)\s*\{([^{}]*)\}/g)) {
    styles.set(name!, `${styles.get(name!) ?? ""};${declarations!.replace(/!important/gi, "")}`);
  }
  return styles;
}

function copyAttribute(target: HTMLElement, tag: string, name: string, value: string): void {
  if (name === KEPT_STYLE) {
    setStyles(target, value);
  } else if (name === "href" && tag === "a") {
    if (!LINK.test(value)) return;
    target.setAttribute("href", value);
    target.setAttribute("target", "_blank");
    target.setAttribute("rel", "noopener noreferrer");
  } else if (name === "src" && tag === "img") {
    const url = urlOf(value);
    if (url !== null && LOGOS.has(url.pathname)) target.setAttribute("src", url.pathname);
    else if (url !== null && SHOP_LOGO.test(url.pathname)) target.setAttribute("src", url.pathname + url.search);
  } else if (ATTRIBUTES.has(name)) {
    target.setAttribute(name, value);
  }
}

/** `dark`: the dark colours per class, laid over the white ones the elements carry themselves. */
function copy(node: Node, dark: Map<string, string>): Node | null {
  if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent ?? "");
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const source = node as Element;
  // the mail's page itself becomes a block: its background is the mail's
  const tag = source.localName === "body" ? "div" : source.localName;
  if (!ELEMENTS.has(tag)) return null;
  const target = document.createElement(tag);
  for (const attribute of source.attributes) copyAttribute(target, tag, attribute.name, attribute.value);
  for (const name of source.classList) setStyles(target, dark.get(name) ?? "");
  for (const child of source.childNodes) {
    const copied = copy(child, dark);
    if (copied !== null) target.append(copied);
  }
  return target;
}

/**
 * The mail as one element, safe to place in the till; an empty block for an empty mail. A mail
 * without a dark version looks the same in both.
 */
export function mailDom(html: string, scheme: MailScheme = "light"): HTMLElement {
  const aside = setStylesAside(html);
  const mail = new DOMParser().parseFromString(aside.html, "text/html");
  return copy(mail.body, scheme === "dark" ? darkVersion(aside.sheets) : new Map<string, string>()) as HTMLElement;
}
